import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, Paper, Stack } from "@mui/material";
import { useApi } from "@platform/api";
import { Button, Typography } from "@platform/design-system";
import {
  CoverageColumn,
  FormState,
  getFormInitState,
  SubmitButton,
} from "@platform/census-components";
import { useLoadRfp } from "@platform/btx-plan-designer";
import { useCallback, useEffect, useRef } from "react";
import { CENSUS_ELIMINATE_REDUNDANCY } from "@platform/feature-flags";

import { ProcessedCensus } from "../../components/processedCensus";
import {
  useCensusDocument,
  UseCensusDocumentReturn,
} from "../../hooks/useCensusDocument";

import { CensusTabCensusForm as CensusForm } from "./CensusTabCensusForm";
import {
  CensusTabCoverageValidations,
  useCoverageValidations,
} from "./CensusTabCoverageValidations";
import { CenteredBox } from "./CenteredBox";
import { RfpCoverages, RfpData } from "./types";

interface CensusTabProps {
  rfpId?: string;
  ecoRfpId?: string;
  resetFormKey: number;
  onClose: VoidFunction;
}

const POLICY_PERIOD = {
  CURRENT: "current",
  NEW_BUSINESS: "newBusiness",
};

const VALID_POLICY_PERIODS = Object.values(POLICY_PERIOD);

function CensusTab({
  rfpId,
  ecoRfpId,
  onClose,
  resetFormKey,
}: CensusTabProps): React.JSX.Element {
  const formInitStateDataRef = useRef(getFormInitState());
  const enableCensusEliminateRedundancy = useFeatureIsOn(
    CENSUS_ELIMINATE_REDUNDANCY,
  );
  const {
    isLoading,
    isError,
    isCensusExists,
    isDeletingCensusDocument,
    onDeleteCensusDocument,
    formState,
    onChange,
    onConfirm,
    updateFormState,
    updateFormStateValue,
    isInvalid,
  } = useCensusDocument(rfpId, {
    useExperimentalCoverageClasses: enableCensusEliminateRedundancy,
  });

  const axiosInstance = useApi();

  const {
    data,
    isLoading: isLoadingRfp,
    error: rfpError,
  } = useLoadRfp(ecoRfpId ?? "", axiosInstance, {
    enabled: !isLoading && !isError && !isCensusExists && !!ecoRfpId,
    staleTime: 0,
    select: (data) => ({
      ...data,
      coverages: data.coverages.filter((c) =>
        c.benefitPlans.some((bp) =>
          VALID_POLICY_PERIODS.includes(bp.policyPeriod),
        ),
      ),
    }),
  });

  // btx RfpData type has an error in the coverage benefitClasses definition
  const rfp = data as unknown as RfpData;

  const resetForm = useCallback((): void => {
    updateFormState(formInitStateDataRef.current);
  }, [updateFormState]);

  // Reset form when resetFormKey changes
  useEffect(() => {
    if (resetFormKey !== 0) {
      resetForm();
    }
  }, [resetFormKey, resetForm]);

  const handleDeleteCensusDocument = async (): Promise<void> => {
    await onDeleteCensusDocument();
    resetForm();
  };

  if (isLoading || isLoadingRfp) {
    return (
      <CenteredBox>
        <Typography>Loading census information...</Typography>
      </CenteredBox>
    );
  }

  if (isError || !rfpId || rfpError) {
    return (
      <CenteredBox>
        <Typography textAlign="center" color="danger">
          <strong>
            Error getting census information. Please try again later!
          </strong>
        </Typography>
      </CenteredBox>
    );
  }

  if (isCensusExists) {
    return (
      <Box py={3}>
        <ProcessedCensus
          rfpId={rfpId}
          onDelete={handleDeleteCensusDocument}
          isDeleting={isDeletingCensusDocument}
        />
      </Box>
    );
  }

  return (
    <Content
      rfpId={rfpId}
      rfp={rfp!}
      onClose={onClose}
      formState={formState}
      isInvalid={isInvalid}
      onChange={onChange}
      onConfirm={onConfirm}
      updateFormState={updateFormState}
      updateFormStateValue={updateFormStateValue}
      formInitStateDataRef={formInitStateDataRef}
    />
  );
}

type ContentProps = {
  rfpId: string;
  rfp: RfpData;
  onClose: VoidFunction;
  formInitStateDataRef: React.RefObject<FormState>;
} & Pick<
  UseCensusDocumentReturn,
  | "formState"
  | "onChange"
  | "onConfirm"
  | "updateFormState"
  | "updateFormStateValue"
  | "isInvalid"
>;

function Content({
  rfpId,
  rfp,
  formState,
  isInvalid,
  onChange,
  onConfirm,
  updateFormState,
  updateFormStateValue,
  onClose,
  formInitStateDataRef,
}: ContentProps): React.JSX.Element {
  const coverageValidations = useCoverageValidations(rfp);

  if (coverageValidations.hasErrors) {
    return (
      <CensusTabCoverageValidations
        rfpId={rfpId}
        onClose={onClose}
        coverageValidations={coverageValidations}
      />
    );
  }

  return (
    <InternalCensusForm
      rfp={rfp}
      formState={formState}
      onChange={onChange}
      onConfirm={onConfirm}
      updateFormState={updateFormState}
      updateFormStateValue={updateFormStateValue}
      isInvalid={isInvalid}
      onClose={onClose}
      formInitStateDataRef={formInitStateDataRef}
    />
  );
}

type InternalCensusFormProps = Pick<
  ContentProps,
  | "rfp"
  | "formState"
  | "onChange"
  | "onConfirm"
  | "updateFormState"
  | "updateFormStateValue"
  | "isInvalid"
  | "onClose"
  | "formInitStateDataRef"
>;

function InternalCensusForm({
  rfp,
  formState,
  onChange,
  onConfirm,
  updateFormState,
  updateFormStateValue,
  isInvalid,
  onClose,
  formInitStateDataRef,
}: InternalCensusFormProps): React.JSX.Element {
  useEffect(() => {
    const selectedCoverages = buildSelectedCoveragesFromRfp(rfp.coverages);
    const coveragesColumns = buildCoveragesColumns(rfp.coverages);
    const censusGroupClasses = buildCensusGroupClasses(coveragesColumns);

    // This sets the initial form state data for later use in resetting the form
    // eslint-disable-next-line react-compiler/react-compiler
    formInitStateDataRef.current = {
      ...formInitStateDataRef.current,
      selectedCoverages,
      coveragesColumns,
      censusGroupClasses,
    };

    updateFormState({
      selectedCoverages,
      coveragesColumns,
      censusGroupClasses,
    });
  }, [rfp.coverages, updateFormState, formInitStateDataRef]);

  return (
    <>
      <Paper variant="outlined" sx={{ m: 2, p: 2 }}>
        <CensusForm
          formState={formState}
          onChange={onChange}
          updateFormStateValue={updateFormStateValue}
        />
      </Paper>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "flex-end", px: 3, pb: 3, pt: 2 }}
      >
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <SubmitButton
          formState={formState}
          handleConfirm={onConfirm}
          updateFormState={updateFormState}
          isInvalid={isInvalid}
        />
      </Stack>
    </>
  );
}

function buildSelectedCoveragesFromRfp(
  coverages: RfpCoverages,
): FormState["selectedCoverages"] {
  const grouped = coverages.reduce<Record<string, RfpCoverages>>(
    (acc, coverage) => ({
      ...acc,
      [coverage.coverageType]: [
        ...(acc[coverage.coverageType] || []),
        coverage,
      ],
    }),
    {},
  );

  return Object.entries(grouped).reduce<FormState["selectedCoverages"]>(
    (acc, [censusName, coverages]) => {
      const builder = COVERAGE_BUILDERS[censusName];
      const entry = builder ? builder(coverages) : { coverage: censusName };
      return {
        ...acc,
        [entry.coverage]: entry,
      };
    },
    {},
  );
}

function getExistingValue(plans: RfpCoverages[number]["benefitPlans"]): string {
  return plans[0]?.policyPeriod === "current" ? "existing" : "new";
}

function hasEmployerPaid(
  benefits: RfpCoverages[number]["benefitPlans"][number]["benefits"],
): boolean {
  return (
    benefits != null &&
    "employerContribution" in benefits &&
    benefits.employerContribution === "oneHundredPercentEmployerPaid"
  );
}

function getEmployerPaidClasses(coverages: RfpCoverages): string[] {
  const allClassNames = coverages.flatMap((coverage) => {
    const employerPaidPlans = coverage.benefitPlans.filter((bp) =>
      hasEmployerPaid(bp.benefits),
    );
    if (employerPaidPlans.length === 0) return [];

    const planLevelClasses = employerPaidPlans.flatMap((bp) =>
      (bp.eligibilityClasses ?? []).map((c) => c.name),
    );

    return planLevelClasses.length > 0
      ? planLevelClasses
      : (coverage.eligibilityClasses ?? []).map((c) => c.name);
  });

  return [...new Set(allClassNames)];
}

function buildDentalEntry(
  coverages: RfpCoverages,
): FormState["selectedCoverages"][string] {
  const groupedByProductType = coverages.reduce<Record<string, RfpCoverages>>(
    (acc, coverage) => {
      const currentBenefitPlans = coverage.benefitPlans.filter(
        (bp) => bp.policyPeriod === POLICY_PERIOD.CURRENT,
      );
      if (currentBenefitPlans.length === 0) {
        return acc;
      }

      return {
        ...acc,
        [coverage.productType]: [
          ...(acc[coverage.productType] ?? []),
          { ...coverage, benefitPlans: currentBenefitPlans },
        ],
      };
    },
    {},
  );

  const coverageNewOption = { coverage: "dental", existing: "new" };
  const keys = Object.keys(groupedByProductType);

  // if we don't have any current coverage, we consider they are all new
  if (keys.length === 0) {
    return coverageNewOption;
  }

  // check if we have a dual/triple option
  const multiOption = getCoverageMultiOptionType(groupedByProductType["ppo"]);
  if (multiOption) {
    return getCoverageExistingOption("dental", multiOption);
  }

  const isHmo = keys.includes("hmo");
  const isPpo = keys.includes("ppo");
  // check if we have hmo and ppo option
  if (isHmo && isPpo) {
    return getCoverageExistingOption("dental", "hmoPpo");
  }

  if (isHmo) {
    return getCoverageExistingOption("dental", "hmo");
  }

  if (isPpo) {
    return getCoverageExistingOption("dental", "ppo");
  }

  return coverageNewOption;
}

function buildVisionEntry(
  coverages: RfpCoverages,
): FormState["selectedCoverages"][string] {
  // check if we have a dual/triple option
  const multiOption = getCoverageMultiOptionType(coverages);

  if (multiOption) {
    return getCoverageExistingOption("vision", multiOption);
  }

  const hasCurrentCoverage = coverages
    .flatMap((c) => c.benefitPlans)
    .some((bp) => bp.policyPeriod === POLICY_PERIOD.CURRENT);

  if (hasCurrentCoverage) {
    return getCoverageExistingOption("vision", "singleVision");
  }

  return { coverage: "vision", existing: "new" };
}

function buildDisabilityEntry(
  censusName: string,
  coverages: RfpCoverages,
): FormState["selectedCoverages"][string] {
  const bp = coverages[0]?.benefitPlans[0];
  if (!bp) return { coverage: censusName };

  const existing = getExistingValue([bp]);
  if (existing !== "existing") {
    return { coverage: censusName, existing };
  }

  const employerPaidClasses = getEmployerPaidClasses(coverages);
  const hasAnyEmployerPaid = employerPaidClasses.length > 0;

  return {
    coverage: censusName,
    existing,
    fullCoveredClasses: hasAnyEmployerPaid ? "yes" : "no",
    ...(hasAnyEmployerPaid && { coverClasses: employerPaidClasses }),
  };
}

function getBenefitStructure(
  isSalaryBased: boolean,
): "Salary-Based" | "Flat Amount" {
  return isSalaryBased ? "Salary-Based" : "Flat Amount";
}

function buildBasicLifeEntry(
  coverages: RfpCoverages,
): FormState["selectedCoverages"][string] {
  const bp = coverages[0]?.benefitPlans[0];
  if (!bp) return { coverage: "basiclife" };

  const existing = getExistingValue([bp]);
  const benefits = bp.benefits;
  const isSalaryBased =
    benefits != null &&
    "benefitAmountType" in benefits &&
    (benefits.benefitAmountType === "salaryMultiplier" ||
      benefits.benefitAmountType === "salaryMultiplierPlusDollarAmount");

  if (existing !== "existing") {
    return {
      coverage: "basiclife",
      existing,
      benefitStructure: getBenefitStructure(isSalaryBased),
    };
  }

  const employerPaidClasses = getEmployerPaidClasses(coverages);
  const hasAnyEmployerPaid = employerPaidClasses.length > 0;

  return {
    coverage: "basiclife",
    existing,
    benefitStructure: getBenefitStructure(isSalaryBased),
    fullCoveredClasses: hasAnyEmployerPaid ? "yes" : "no",
    ...(hasAnyEmployerPaid && { coverClasses: employerPaidClasses }),
  };
}

function buildVoluntaryLifeEntry(
  coverages: RfpCoverages,
): FormState["selectedCoverages"][string] {
  const bp = coverages[0]?.benefitPlans[0];
  if (!bp) return { coverage: "voluntarylife" };

  const existing = getExistingValue([bp]);
  const benefits = bp.benefits;
  const isSalaryBased =
    benefits != null &&
    "benefitDescriptionEmployeeType" in benefits &&
    benefits.benefitDescriptionEmployeeType === "salaryMultiplier";

  return {
    coverage: "voluntarylife",
    existing,
    benefitStructure: getBenefitStructure(isSalaryBased),
  };
}

const COVERAGE_BUILDERS: Record<
  string,
  (coverages: RfpCoverages) => FormState["selectedCoverages"][string]
> = {
  dental: buildDentalEntry,
  vision: buildVisionEntry,
  std: (coverages) => buildDisabilityEntry("std", coverages),
  ltd: (coverages) => buildDisabilityEntry("ltd", coverages),
  basicLifeAdnd: buildBasicLifeEntry,
  voluntaryLifeAdnd: buildVoluntaryLifeEntry,
};

function buildCensusGroupClasses(coveragesColumns: CoverageColumn[]): string[] {
  const allClasses = coveragesColumns.flatMap((cc) =>
    cc.classes.map((c) => c.name),
  );
  return [...new Set(allClasses)];
}

function buildCoveragesColumns(coverages: RfpCoverages): CoverageColumn[] {
  const coveragesColumns = coverages.reduce<Record<string, CoverageColumn>>(
    (acc, coverage) =>
      coverage.benefitClasses.length > 0 && !acc[coverage.coverageType]
        ? {
            ...acc,
            [coverage.coverageType]: {
              coverageType: coverage.coverageType,
              classes: coverage.benefitClasses,
              columnLetter: "",
            },
          }
        : acc,
    {},
  );
  return Object.values(coveragesColumns);
}

function getCoverageMultiOptionType(
  coverages: RfpCoverages = [],
): string | null {
  const optionsNames = coverages
    .flatMap((c) => c.benefitPlans)
    .reduce<Set<string>>((acc, bp) => {
      if (
        bp.policyPeriod === POLICY_PERIOD.CURRENT &&
        ["low", "medium", "high"].includes(bp.optionName)
      ) {
        acc.add(bp.optionName);
      }
      return acc;
    }, new Set<string>());

  const optionNamesSize = optionsNames.size;
  switch (optionNamesSize) {
    case 3:
      return "tripleOption";
    case 2:
      return "dualOption";
    default:
      return null;
  }
}

const getCoverageExistingOption = (
  coverage: string,
  value: string,
): FormState["selectedCoverages"][string] => ({
  coverage,
  existing: "existing",
  coverageOption: { value, plans: {} },
});

export default CensusTab;
